import type { User } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient.ts';

/**
 * Metadata a public signup stashes on the auth user so the student record can be
 * created later — email confirmation means there's no session at signup time.
 */
export type StudentSignupMetadata = {
  signup_role?: string;
  first_name?: string;
  last_name?: string;
  signup_year_id?: string | null;
  signup_subject_ids?: string[];
};

/**
 * Create the student record for a confirmed public signup, if it doesn't exist.
 *
 * Public students have no company (students.company_id is null); their subject
 * picks are what give the app a curriculum to work from. Safe to call on every
 * sign-in: it returns the existing row when there is one, and only ever creates
 * a record for users who signed up here as a student.
 *
 * Returns the student id, or null if this user isn't a public signup.
 */
export async function provisionStudentAccount(
  user: User,
  extras?: StudentSignupMetadata,
): Promise<string | null> {
  const existingId = await findStudentId(user.id);
  if (existingId) return existingId;

  const meta = {
    ...((user.user_metadata ?? {}) as StudentSignupMetadata),
    ...extras,
  };
  if (meta.signup_role !== 'student') return null;

  const { data: created, error } = await supabase
    .from('students')
    .insert({
      user_id: user.id,
      company_id: null,
      first_name: meta.first_name ?? null,
      last_name: meta.last_name ?? null,
      email: user.email ?? null,
      year_id: meta.signup_year_id ?? null,
    })
    .select('id')
    .single();

  if (error) {
    // students.user_id is unique, so a duplicate means another tab got there
    // first — use whatever it created rather than failing the sign-in.
    const raced = await findStudentId(user.id);
    if (raced) return raced;
    throw new Error(error.message);
  }

  const subjectIds = Array.isArray(meta.signup_subject_ids) ? meta.signup_subject_ids : [];
  if (subjectIds.length) {
    // Subjects are a nice-to-have next to having an account at all, so a failure
    // here shouldn't block sign-in. They can be fixed up from the profile later.
    const { error: subjectErr } = await supabase
      .from('student_subjects')
      .insert(subjectIds.map((subject_id) => ({ student_id: created.id, subject_id })));
    if (subjectErr) console.error('Failed to save signup subjects:', subjectErr);
  }

  return created.id as string;
}

export async function findStudentId(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from('students')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();
  return (data?.id as string | undefined) ?? null;
}
